import { useState } from 'react';
import { PageBody, PageHeader, ScopeBar } from '../components/AppShell';
import {
  Button,
  Card,
  CardHeader,
  Empty,
  ErrorState,
  Icon,
  Loading,
  Pill,
  Segmented,
} from '../components/ui';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { dateTime } from '../lib/format';
import { useApi } from '../lib/useApi';
import './shared.css';

interface BinEntry {
  kind: string;
  label: string;
  id: string;
  name: string;
  purgedAt?: string;
  purgedByName?: string;
}

type Filter = 'all' | 'org_nodes' | 'users' | 'applications' | 'merchants' | 'orders';

const FILTER_LABEL: Record<Filter, string> = {
  all: 'Everything',
  org_nodes: 'Offices',
  users: 'People',
  applications: 'Applications',
  merchants: 'Merchants',
  orders: 'Orders',
};

/**
 * The recycle bin.
 *
 * Nothing in this portal is removed outright — deleting flags a document and
 * takes it off every screen, and this is where it waits. Restoring is the
 * ordinary path; emptying it for good is an administrator's decision and
 * cannot be undone, which is why the two actions do not look alike.
 */
export function RecycleBin() {
  const { user } = useAuth();
  const [filter, setFilter] = useState<Filter>('all');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const { data, error: loadError, loading, reload } = useApi<BinEntry[]>('/recycle-bin');
  const isAdmin = user?.role === 'l1_admin';

  // Same reason as the People screen: restoring something reloads the list,
  // and blanking the page for a loader would take the "it is back" notice
  // down with it before anyone could read it.
  if (loading && !data) return <Loading label="Opening the recycle bin…" />;
  if (loadError) return <ErrorState error={loadError} onRetry={reload} />;

  const entries = (data ?? []).filter((e) => filter === 'all' || e.kind === filter);
  const counts = (data ?? []).reduce<Record<string, number>>((acc, e) => {
    acc[e.kind] = (acc[e.kind] ?? 0) + 1;
    return acc;
  }, {});

  async function act(entry: BinEntry, action: 'restore' | 'purge') {
    setBusy(entry.id);
    setError(null);
    setNotice(null);
    try {
      if (action === 'restore') {
        const res = await api.post<{ documentsRestored?: number }>(
          `/recycle-bin/${entry.kind}/${entry.id}/restore`,
        );
        const n = res?.documentsRestored ?? 1;
        setNotice(
          n > 1
            ? `${entry.name} is back, along with the ${n - 1} records deleted with it.`
            : `${entry.name} is back.`,
        );
      } else {
        await api.del(`/recycle-bin/${entry.kind}/${entry.id}`);
        setNotice(`${entry.name} is gone for good.`);
      }
      setConfirming(null);
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <PageHeader
        breadcrumb={['Administration', 'Recycle bin']}
        title="Recycle bin"
        actions={
          <Pill tone={data?.length ? 'warn' : 'neutral'}>
            {data?.length ?? 0} item{data?.length === 1 ? '' : 's'}
          </Pill>
        }
      />

      <ScopeBar>
        Everything deleted beneath <strong>{user?.nodeName}</strong>. Restoring an
        office brings back everything that was deleted with it.
      </ScopeBar>

      <PageBody>
        {error && (
          <div className="notice notice--danger" role="alert">
            {Icon.alert({ size: 15 })}
            <span>{error}</span>
          </div>
        )}
        {notice && (
          <div className="notice notice--ok" role="status">
            {Icon.check({ size: 15 })}
            <span>{notice}</span>
          </div>
        )}

        <Card>
          <CardHeader title="Deleted" count={entries.length} />

          {(data?.length ?? 0) > 0 && (
            <div style={{ marginBottom: 14 }}>
              <Segmented<Filter>
                label="Filter by kind"
                value={filter}
                onChange={setFilter}
                options={(Object.keys(FILTER_LABEL) as Filter[])
                  .filter((k) => k === 'all' || counts[k])
                  .map((k) => ({
                    value: k,
                    label: FILTER_LABEL[k],
                    count: k === 'all' ? data?.length : counts[k],
                  }))}
              />
            </div>
          )}

          {entries.length === 0 ? (
            <Empty
              title="Nothing deleted"
              detail="Anything you delete lands here and can be put back. Offices arrive with everything that was under them."
            />
          ) : (
            <div className="stack" style={{ gap: 12 }}>
              {entries.map((entry) => (
                <div key={`${entry.kind}:${entry.id}`} className="person">
                  <div className="person__body">
                    <div className="person__head">
                      <strong>{entry.name}</strong>
                      <Pill tone="neutral">{entry.label}</Pill>
                    </div>
                    <div className="person__meta">
                      deleted {entry.purgedAt ? dateTime(entry.purgedAt) : 'at some point'}
                      {entry.purgedByName ? ` by ${entry.purgedByName}` : ''}
                    </div>

                    {confirming === entry.id ? (
                      <div className="notice notice--danger" style={{ marginTop: 9 }}>
                        {Icon.alert({ size: 15 })}
                        <span>
                          Permanently delete <strong>{entry.name}</strong>? This
                          cannot be undone — only the audit entry survives it.
                        </span>
                      </div>
                    ) : null}

                    <div className="row" style={{ gap: 7, marginTop: 9 }}>
                      <Button
                        disabled={busy === entry.id}
                        onClick={() => void act(entry, 'restore')}
                      >
                        Restore
                      </Button>
                      {isAdmin &&
                        (confirming === entry.id ? (
                          <>
                            <Button
                              variant="danger"
                              disabled={busy === entry.id}
                              onClick={() => void act(entry, 'purge')}
                            >
                              Yes, delete permanently
                            </Button>
                            <Button onClick={() => setConfirming(null)}>Keep it</Button>
                          </>
                        ) : (
                          <Button onClick={() => setConfirming(entry.id)}>
                            Delete permanently…
                          </Button>
                        ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </PageBody>
    </>
  );
}
